import { useQuery } from '@tanstack/react-query';
import { useEffect, useId, useRef, useState } from 'react';
import type { GeoPoint } from '@app/shared/geo';
import { api, ApiError } from '../api';
import { DialogPortal } from '../ui/dialogHost';
import { trapTab } from '../ui/focus';
import { MapPreview } from './MapPreview';
import { routeLine, type ChosenRoute, type RouteOption } from './place';
import { DialogLayer } from '../ui/Dialog';
import { leaveGuarded } from '../ui/unsaved';

/** The route chosen before, among the routes now (the same km and label), or Google's proposal. */
function initialIndex(routes: RouteOption[], chosen: ChosenRoute | null): number {
  const same = chosen ? routes.findIndex((r) => r.km === chosen.km && r.label === chosen.label) : -1;
  if (same >= 0) return same;
  return Math.max(0, routes.findIndex((r) => r.isDefault));
}

/**
 * "Pokaż trasę" (owner 2026-10-21 22:46; spec 2026-10-22-mapy-google §4.3): Google's routes from the warehouse — up to
 * three, with km, travel time and tolls — on the Google map (OpenStreetMap: the list only, Google's terms). Google's
 * proposal is marked; "Użyj tej trasy" hands the chosen one over (its km then count before anything else).
 * "Unikaj płatnych" asks again without toll roads. Whoever may not change the event only looks.
 */
export function RouteDialog({
  to,
  startsAt,
  avoidTolls,
  chosen,
  canChoose,
  onUse,
  onClose,
}: {
  to: GeoPoint;
  startsAt: string | null;
  avoidTolls: boolean;
  chosen: ChosenRoute | null;
  canChoose: boolean;
  onUse: (route: ChosenRoute, avoidTolls: boolean) => void;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const name = useId();
  const [avoid, setAvoid] = useState(avoidTolls);
  const [picked, setPicked] = useState<number | null>(null);
  const routes = useQuery({
    queryKey: ['route-options', to.lat, to.lng, avoid, startsAt],
    retry: false,
    staleTime: 5 * 60_000,
    queryFn: () => api<{ routes: RouteOption[]; attribution: string }>('/api/events/route-options', { method: 'POST', body: { to, avoidTolls: avoid, ...(startsAt ? { startsAt } : {}) } }),
  });
  const list = routes.data?.routes ?? [];
  const index = picked ?? initialIndex(list, avoid === avoidTolls ? chosen : null);
  const selected = list[index] ?? null;

  // the heading takes the focus; `DialogLayer` handles Escape and gives the focus back
  useEffect(() => {
    ref.current?.querySelector<HTMLElement>('h2')?.focus();
  }, []);

  return (
    <DialogPortal fullWindow>
      <div className="dialog-backdrop map-dialog-backdrop" onPointerDown={(e) => e.target === e.currentTarget && leaveGuarded(onClose, e.currentTarget)}>
        <div
          ref={ref}
          className="dialog panel map-dialog route-dialog"
          role="dialog"
          aria-modal="true"
          aria-label="Trasa z magazynu"
          data-feature-id="events.route"
          onKeyDown={(e) => {
            if (e.key === 'Tab') trapTab(e.currentTarget, e.nativeEvent);
          }}
        >
          <div className="map-dialog-head">
            <h2 tabIndex={-1}>Trasa z magazynu</h2>
            <DialogLayer onClose={onClose} />
            <button className="btn small ghost" type="button" onClick={onClose}>
              Zamknij
            </button>
          </div>
          {list.length > 0 && (
            <MapPreview
              label="Mapa tras"
              className="map-preview route-map"
              point={to}
              routes={list.map((r, i) => ({ key: String(i), polyline: r.polyline, selected: i === index }))}
              onRouteClick={(key) => setPicked(Number(key))}
              wheelZoom
            />
          )}
          {routes.isFetching && <p className="hint">Pytam Google o trasy…</p>}
          {routes.error && (
            <p className="alert" role="alert">
              {routes.error instanceof ApiError ? routes.error.message : 'Nie udało się pobrać tras'}
            </p>
          )}
          {list.length > 0 && (
            <fieldset className="route-options">
              <legend className="visually-hidden">Trasy</legend>
              {list.map((r, i) => (
                <label key={`${r.label}-${r.km}`} className={`route-option${i === index ? ' chosen' : ''}`}>
                  <input type="radio" name={name} checked={i === index} disabled={!canChoose && i !== index} onChange={() => setPicked(i)} />
                  <span>{routeLine(r)}</span>
                  {r.isDefault && <span className="muted route-default">proponowana przez Google</span>}
                </label>
              ))}
            </fieldset>
          )}
          <div className="map-dialog-foot">
            <p className="map-dialog-status">
              {canChoose && (
                <label className="check">
                  <input
                    type="checkbox"
                    checked={avoid}
                    onChange={(e) => {
                      setAvoid(e.target.checked);
                      setPicked(null);
                    }}
                  />
                  <span>Unikaj płatnych</span>
                </label>
              )}
              <span className="hint">Opłaty to szacunek Google dla samochodu osobowego, w jedną stronę.</span>
              <span className="route-attribution" translate="no">
                Google Maps
              </span>
            </p>
            <div className="map-dialog-actions">
              <button className="btn" type="button" onClick={onClose}>
                {canChoose ? 'Anuluj' : 'Zamknij'}
              </button>
              {canChoose && (
                <button
                  className="btn primary"
                  type="button"
                  disabled={!selected}
                  onClick={() => {
                    if (!selected) return;
                    onUse(
                      {
                        routeId: selected.routeId,
                        km: selected.km,
                        ...(selected.minutes !== undefined ? { minutes: selected.minutes } : {}),
                        ...(selected.tollsGrosze !== undefined ? { tollsGrosze: selected.tollsGrosze } : {}),
                        label: selected.label,
                      },
                      avoid,
                    );
                    onClose();
                  }}
                >
                  Użyj tej trasy
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </DialogPortal>
  );
}
